import { type Href, useLocalSearchParams } from 'expo-router';
import Head from 'expo-router/head';
import CalendarDays from 'lucide-react-native/icons/calendar-days';
import Compass from 'lucide-react-native/icons/compass';
import Sailboat from 'lucide-react-native/icons/sailboat';
import { useRef } from 'react';
import { ScrollView, StyleSheet, Text, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useTranslation } from 'react-i18next';

import { CityHotels } from '@/components/detail/hotels-nearby';
import { listSeedScript } from '@/components/detail/list-seed';
import { BoatCard } from '@/components/riviera/boat-card';
import { CardCarousel } from '@/components/riviera/card-carousel';
import { CityGuideBlock } from '@/components/riviera/city-guide';
import { CityHero } from '@/components/riviera/city-hero';
import type { CityHeroChip } from '@/components/riviera/city-hero.types';
import { EventCard } from '@/components/riviera/event-card';
import { navHref } from '@/components/riviera/nav-links';
import { SiteFooter } from '@/components/riviera/site-footer';
import { SiteHeader } from '@/components/riviera/site-header';
import { TourCard } from '@/components/riviera/tour-card';
import { MaxContentWidth, Spacing } from '@/constants/theme';
import { useBoats } from '@todo/core/hooks/use-boats';
import { useCityListingPages } from '@/hooks/use-city-listing-pages';
import { useEvents } from '@todo/core/hooks/use-events';
import { useTours } from '@todo/core/hooks/use-tours';
import { useTheme } from '@/hooks/use-theme';
import { MIN_BOATS_PER_CITY_PAGE } from '@/lib/boat-rental';
import { cities } from '@/lib/events-data';
import { localizedTitle } from '@todo/core/i18n/event-content';
import { boatCityIntro, localizedBoatTitle } from '@/lib/i18n/boat-content';
import { localizedTourTitle } from '@todo/core/i18n/tour-content';
import { cityGuide, cityGuideFaqHeading, cityPageCopy } from '@/lib/i18n/city-content';
import { cityClimateCopy, cityClimateFaq } from '@/lib/i18n/climate-content';
import { useLocale } from '@todo/core/i18n/use-locale';
import {
  CITY_LISTING_PAGES_SEED_KEY,
  cityBoatsQuery,
  cityBoatsSeedKey,
  cityEventsQuery,
  cityEventsSeedKey,
  cityToursQuery,
  cityToursSeedKey,
} from '@todo/core/list-queries';
import {
  SITE_NAME,
  absoluteUrl,
  breadcrumbJsonLd,
  faqPageJsonLd,
  hreflangLinks,
  itemListJsonLd,
  placeJsonLd,
  richResultImage,
  truncate,
} from '@todo/core/seo';

export { generateCityStaticParams as generateStaticParams } from '@/lib/detail/city-detail';

// The city landing page — "things to do in {city}", automatable from the same
// event_shows/tours_by_city RPCs the /events and /tours hubs already use,
// scoped to one city_id. Modeled on those hubs (not on ArticleHead's
// per-locale-slug handling), since a city slug is identical across locales.
// The only hand-authored part is the intro copy (src/lib/i18n/city-content.ts).
//
// Layout: a boxed hero (breadcrumb, h1, "jump to section" chips) inside the
// content column, a reading-width intro, then one horizontal card carousel
// per catalogue — see city-hero.module.css / card-carousel.web.tsx for why
// it's no longer a full-bleed hero over three wrapping card grids.

type SectionId = 'events' | 'tours' | 'boats';

/** "24+" once a list hits its query limit — the city may well have more. */
function countLabel(count: number, limit: number | undefined): string {
  return limit != null && count >= limit ? `${limit}+` : String(count);
}

export default function CityScreen() {
  const theme = useTheme();
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { city: cityParam } = useLocalSearchParams<{ city: string }>();
  const city = cities.find((c) => c.id === cityParam);
  const copy = city ? cityPageCopy(city.id, locale) : undefined;
  const boatIntro = city ? boatCityIntro(city.id, locale) : undefined;
  const authoredGuide = city ? cityGuide(city.id, locale) : undefined;
  const climate = city ? cityClimateCopy(city.id, locale, t) : undefined;
  // The climate FAQ entries go after the hand-written ones, in both the
  // visible FAQ and its JSON-LD.
  const guide =
    authoredGuide && city
      ? { ...authoredGuide, faq: [...authoredGuide.faq, ...cityClimateFaq(city.id, locale, t)] }
      : undefined;

  // Always called (rules of hooks) — cityParam is only ever missing/invalid
  // via a bad client-side navigation, since generateCityStaticParams only ever
  // emits the 7 known ids. An unknown id just returns no rows.
  const eventsQuery = cityEventsQuery(cityParam ?? '');
  const toursQuery = cityToursQuery(cityParam ?? '');
  const boatsQuery = cityBoatsQuery(cityParam ?? '');
  const { events } = useEvents(eventsQuery, city ? cityEventsSeedKey(city.id) : undefined);
  const { tours } = useTours(toursQuery, city ? cityToursSeedKey(city.id) : undefined);
  const { boats } = useBoats(boatsQuery, city ? cityBoatsSeedKey(city.id) : undefined);
  const listingPages = useCityListingPages();

  const scrollRef = useRef<ScrollView>(null);
  const contentRef = useRef<View>(null);
  const sectionRefs = {
    events: useRef<View>(null),
    tours: useRef<View>(null),
    boats: useRef<View>(null),
  };

  if (!city || !copy) {
    return (
      <View style={[styles.root, { backgroundColor: theme.background }]}>
        <Head key={locale}>
          <title>{`${t('common.notFound')} — ${SITE_NAME}`}</title>
          <meta name="robots" content="noindex, follow" />
        </Head>
        <SafeAreaView edges={['top']} style={{ backgroundColor: theme.card }}>
          <SiteHeader />
        </SafeAreaView>
        <Text style={[styles.status, { color: theme.mutedForeground }]}>
          {t('common.notFound')}
        </Text>
      </View>
    );
  }

  const canonical = absoluteUrl(locale, city.id);
  const metaDescription = truncate(copy.metaDescription);
  const ogImage = richResultImage(
    events[0]?.image_url ?? tours[0]?.image_url ?? boats[0]?.image_url ?? null,
  );

  const chips: (CityHeroChip & { id: SectionId })[] = [];
  if (events.length > 0) {
    chips.push({
      id: 'events',
      icon: CalendarDays,
      label: t('cityHero.events', {
        count: events.length,
        display: countLabel(events.length, eventsQuery.limit),
      }),
    });
  }
  if (tours.length > 0) {
    chips.push({
      id: 'tours',
      icon: Compass,
      label: t('cityHero.tours', {
        count: tours.length,
        display: countLabel(tours.length, toursQuery.limit),
      }),
    });
  }
  if (boats.length > 0) {
    chips.push({
      id: 'boats',
      icon: Sailboat,
      label: t('cityHero.boats', {
        count: boats.length,
        display: countLabel(boats.length, boatsQuery.limit),
      }),
    });
  }

  // "See all" goes to the city's own /[city]/events and /[city]/tours pages
  // when they were exported, else to the regional hub.
  const hasEventsPage = listingPages.events.includes(city.id);
  const cityEventsHref: Href = hasEventsPage
    ? { pathname: '/[lang]/[city]/events', params: { lang: locale, city: city.id } }
    : { pathname: '/[lang]/events', params: { lang: locale } };
  const cityToursHref: Href = listingPages.tours.includes(city.id)
    ? { pathname: '/[lang]/[city]/tours', params: { lang: locale, city: city.id } }
    : { pathname: '/[lang]/tours', params: { lang: locale } };
  const boatsHref: Href =
    boats.length >= MIN_BOATS_PER_CITY_PAGE
      ? { pathname: '/[lang]/boat-rental/[city]', params: { lang: locale, city: city.id } }
      : { pathname: '/[lang]/boats', params: { lang: locale } };
  // A short city list (e.g. 2 events) already *is* the whole city hub, so its
  // "see more" tile points region-wide instead of back at the same 2 events —
  // unless the city has its own events page, which lists more than this slice.
  const eventsFull =
    hasEventsPage || (eventsQuery.limit != null && events.length >= eventsQuery.limit);

  // measureLayout against the scroll content works the same on native and
  // react-native-web, so one code path serves both (no DOM ids / hash links —
  // a fragment URL would also round-trip through expo-router's history).
  const scrollToSection = (id: string) => {
    const section = sectionRefs[id as SectionId]?.current;
    const content = contentRef.current;
    if (!section || !content) return;
    section.measureLayout(content, (_x, y) => {
      scrollRef.current?.scrollTo({ y: Math.max(0, y - Spacing.three), animated: true });
    });
  };

  return (
    <View style={[styles.root, { backgroundColor: theme.background }]}>
      {/* key forces a fresh mount on locale change — see the comment in
          app/[lang]/_layout.tsx for why. */}
      <Head key={locale}>
        <title>{`${copy.title} — ${SITE_NAME}`}</title>
        <meta name="description" content={metaDescription} />
        <link rel="canonical" href={canonical} />
        {hreflangLinks(city.id).map((link) => (
          <link key={link.hrefLang} rel="alternate" hrefLang={link.hrefLang} href={link.href} />
        ))}
        <meta property="og:type" content="website" />
        <meta property="og:title" content={copy.title} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:url" content={canonical} />
        {ogImage ? <meta property="og:image" content={ogImage} /> : null}
        <meta name="twitter:title" content={copy.title} />
        <meta name="twitter:description" content={metaDescription} />
        {ogImage ? <meta name="twitter:image" content={ogImage} /> : null}
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbJsonLd(locale, [
              { name: t('nav.cities'), path: 'cities' },
              { name: city.name },
            ]))}
        </script>
        <script type="application/ld+json">{JSON.stringify(placeJsonLd(city.name, canonical))}</script>
        {guide && guide.faq.length > 0 ? (
          <script type="application/ld+json">{JSON.stringify(faqPageJsonLd(guide.faq))}</script>
        ) : null}
        {events.length > 0 ? (
          <script type="application/ld+json">
            {JSON.stringify(
              itemListJsonLd(
                events.map((event) => ({
                  name: localizedTitle(event, locale),
                  url: absoluteUrl(locale, `events/${event.show_slug}`),
                })),
                canonical,
              ),
            )}
          </script>
        ) : null}
        {tours.length > 0 ? (
          <script type="application/ld+json">
            {JSON.stringify(
              itemListJsonLd(
                tours.map((tour) => ({
                  name: localizedTourTitle(tour, locale),
                  url: absoluteUrl(locale, `tours/${tour.id}`),
                })),
                canonical,
              ),
            )}
          </script>
        ) : null}
        {boats.length > 0 ? (
          <script type="application/ld+json">
            {JSON.stringify(
              itemListJsonLd(
                boats.map((boat) => ({
                  name: localizedBoatTitle(boat, locale),
                  url: absoluteUrl(locale, `boats/${boat.id}`),
                })),
                canonical,
              ),
            )}
          </script>
        ) : null}
        {/* Baked rows so the client's first render matches this static HTML. */}
        {listSeedScript(cityEventsSeedKey(city.id))}
        {listSeedScript(cityToursSeedKey(city.id))}
        {listSeedScript(cityBoatsSeedKey(city.id))}
        {listSeedScript(CITY_LISTING_PAGES_SEED_KEY)}
      </Head>
      <SafeAreaView edges={['top']} style={{ backgroundColor: theme.card }}>
        <SiteHeader />
      </SafeAreaView>
      <ScrollView
        ref={scrollRef}
        style={{ flex: 1 }}>
        <View ref={contentRef} style={styles.centered}>
          <View style={styles.page}>
            <CityHero
              image={city.heroImage}
              imageSm={city.heroImageSm}
              alt={city.name}
              title={copy.title}
              crumbs={[
                { label: t('common.home'), href: navHref('/[lang]', locale) },
                { label: t('nav.cities'), href: navHref('/[lang]/cities', locale) },
              ]}
              chips={chips}
              onChipPress={scrollToSection}
            />

            <View style={styles.introBlock}>
              {copy.intro.map((paragraph, index) => (
                <Text key={index} style={[styles.intro, { color: theme.text }]}>
                  {paragraph}
                </Text>
              ))}
            </View>

            {events.length > 0 ? (
              <View ref={sectionRefs.events} style={styles.section}>
                <CardCarousel
                  heading={t('events.headingCity', { city: city.name })}
                  seeAllHref={cityEventsHref}
                  moreTile={
                    eventsFull
                      ? {
                          title: t('events.moreUpcomingCity', { city: city.name }),
                          description: t('events.subheading'),
                          href: cityEventsHref,
                        }
                      : {
                          title: t('events.moreUpcoming'),
                          description: t('events.subheading'),
                          href: navHref('/[lang]/events', locale),
                        }
                  }>
                  {events.map((event, index) => (
                    <EventCard key={event.id} event={event} eager={index < 4} />
                  ))}
                </CardCarousel>
              </View>
            ) : null}

            {tours.length > 0 ? (
              <View ref={sectionRefs.tours} style={styles.section}>
                <CardCarousel
                  heading={t('tours.headingCity', { city: city.name })}
                  seeAllHref={cityToursHref}
                  moreTile={{
                    title: t('tours.moreInCity', { city: city.name }),
                    description: t('tours.subheading'),
                    href: cityToursHref,
                  }}>
                  {tours.map((tour) => (
                    <TourCard key={tour.id} tour={tour} />
                  ))}
                </CardCarousel>
              </View>
            ) : null}

            {boats.length > 0 ? (
              <View ref={sectionRefs.boats} style={styles.section}>
                <CardCarousel
                  heading={t('boats.headingCity', { city: city.name })}
                  description={boatIntro}
                  seeAllHref={boatsHref}
                  moreTile={{
                    title: t('boats.moreInCity', { city: city.name }),
                    description: t('boats.subheading'),
                    href: boatsHref,
                  }}>
                  {boats.map((boat) => (
                    <BoatCard key={boat.id} boat={boat} />
                  ))}
                </CardCarousel>
              </View>
            ) : null}

            <View style={styles.section}>
              <CityHotels cityId={city.id} campaign="city" pageId={city.id} />
            </View>

            {guide ? (
              <CityGuideBlock
                guide={guide}
                climate={climate}
                faqHeading={cityGuideFaqHeading(locale)}
              />
            ) : null}
          </View>
        </View>
        <SiteFooter />
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
  },
  centered: {
    alignItems: 'center',
    width: '100%',
  },
  page: {
    width: '100%',
    maxWidth: MaxContentWidth,
    paddingHorizontal: Spacing.three,
    paddingTop: Spacing.three,
    gap: Spacing.four,
  },
  introBlock: {
    gap: Spacing.three,
  },
  intro: {
    fontSize: 16,
    lineHeight: 26,
  },
  section: {
    marginTop: Spacing.two,
  },
  status: {
    fontSize: 14,
    paddingHorizontal: Spacing.three,
    paddingVertical: Spacing.four,
  },
});
